import React from 'react';
import {
  ShieldCheck,
  FileCheck,
  MapPin,
  Camera,
  AlertTriangle,
  HelpCircle,
  CheckCircle2,
  Lock,
  ArrowRight,
  PhoneCall,
  Scale,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { TELEGRAM_CHAT_URL, TELEGRAM_BOT_USERNAME } from '../../config/telegram';

export const TrustVerificationPage: React.FC = () => {
  const { navigateTo } = useApp();

  const verificationStages = [
    {
      title: '1. Government Identity Verification',
      badge: 'Identity Match',
      desc: 'Professionals submit official Nigerian identification (NIN Slip, NIN Card, International Passport, Driver’s License, or Voter’s Card). We verify the legitimacy of the ID format and cross-match the legal full name against national registry standards.',
      icon: FileCheck,
    },
    {
      title: '2. Operating Base & Utility Proof',
      desc: 'Professionals submit recent Nigerian utility documentation (PHCN electricity receipt, water bill, waste management bill, or tenancy agreement) to verify their physical workshop, office, or residential operating base.',
      icon: MapPin,
    },
    {
      title: '3. Geographic Service Area Confirmation',
      desc: 'Professionals confirm the specific Nigerian States and Local Government Areas (LGAs) they operate in. This prevents out-of-boundary quoting and helps ensure timely response to urgent emergency calls.',
      icon: ShieldCheck,
    },
    {
      title: '4. Real-time Facial Liveness Check',
      desc: 'Professionals undergo a live webcam or mobile camera liveness scan using biometric facial analysis. This confirms that the person registering is the living individual shown on the uploaded government identification, preventing identity theft and fake account creation.',
      icon: Camera,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-12">
      {/* Hero Section */}
      <div className="max-w-5xl mx-auto bg-slate-900 text-white rounded-3xl p-6 sm:p-12 border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
        <div className="relative max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Platform Trust Framework</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
            Trust & Professional Verification at JobMatch NG
          </h1>

          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            We believe accountability is the foundation of dependable technical services in Nigeria.
            Learn how we verify professionals and what our verification badges mean for customers.
          </p>

          <div className="pt-2 flex flex-wrap gap-3">
            <button
              onClick={() => navigateTo('find_pro')}
              className="px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-lg transition-all inline-flex items-center gap-2 cursor-pointer"
            >
              <span>Browse Verified Directory</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigateTo('become_pro')}
              className="px-5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs sm:text-sm font-semibold transition-all inline-flex items-center gap-2 cursor-pointer"
            >
              <span>Professional Verification Guide</span>
            </button>
          </div>
        </div>
      </div>

      {/* 4 Pillars of Verification */}
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            Our 4-Stage Professional Verification Process
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-2xl mx-auto">
            Before a professional is granted a Verified badge and permitted to bid on customer repair requests,
            they must complete all four sequential verification checks.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {verificationStages.map((stage) => {
            const Icon = stage.icon;
            return (
              <div
                key={stage.title}
                className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="space-y-2 flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-base text-slate-900">{stage.title}</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{stage.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Critical Limitation & Clear Disclaimer */}
      <div className="max-w-4xl mx-auto bg-amber-50/70 border border-amber-200 rounded-3xl p-6 sm:p-8 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h3 className="text-base font-extrabold text-amber-950">
              Important Verification Disclaimer & Customer Guidance
            </h3>
            <p className="text-xs sm:text-sm text-amber-900 leading-relaxed font-medium">
              Verification helps JobMatch NG confirm information provided by professionals, including their
              legal identity document, physical operating base, and face matching.
            </p>
            <p className="text-xs sm:text-sm text-amber-900/90 leading-relaxed">
              <strong>Please Note:</strong> Verification does <em>not</em> guarantee absolute honesty, individual
              temperament, perfection in craftsmanship, complete job safety, uninterrupted completion, or absence
              of prior or future misconduct.
            </p>
            <p className="text-xs sm:text-sm text-amber-900/90 leading-relaxed">
              Customers must always exercise reasonable judgment when hiring: confirm quotation items and material
              specifications in writing, supervise repairs on their property, and inspect completed tasks before
              settling final balances.
            </p>
          </div>
        </div>
      </div>

      {/* Reporting Issues & Trust Desk */}
      <div className="max-w-4xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xs space-y-6">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
          <Scale className="w-6 h-6 text-emerald-600 shrink-0" />
          <div>
            <h3 className="font-extrabold text-lg text-slate-900">
              JobMatch NG Trust & Safety Desk
            </h3>
            <p className="text-xs text-slate-500">
              We investigate all customer and professional grievances promptly and objectively.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm text-slate-700">
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
            <h4 className="font-bold text-slate-900">How to Report Misconduct</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              If a professional charges unauthorized fees, behaves unprofessionally, or provides substandard work,
              email us at <strong className="text-slate-900">support@jobmatch.ng</strong> with your job ID.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
            <h4 className="font-bold text-slate-900">Direct Support on Telegram</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              You can reach our assistance desk on Telegram at{' '}
              <a
                href={TELEGRAM_CHAT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-700 font-bold underline font-mono"
              >
                @{TELEGRAM_BOT_USERNAME}
              </a>{' '}
              for real-time escalation and assistance.
            </p>
          </div>
        </div>
      </div>

      {/* Final Action CTA */}
      <div className="max-w-4xl mx-auto bg-slate-900 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-xl">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
          Experience Verified Service Delivery
        </h2>
        <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
          Post your repair job today and connect with screened professionals who take accountability seriously.
        </p>

        <button
          onClick={() => navigateTo('post_job')}
          className="px-8 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-lg shadow-emerald-600/30 transition-all inline-flex items-center gap-2 cursor-pointer"
        >
          <span>Post a Job Free</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
